import type { ChangeEvent, FC, MouseEvent } from "react";
import { useMemo } from "react";
import cs from "classnames";

import {
  Box,
  Grid,
  IconButton,
  SwipeableDrawer,
  Typography,
} from "@braintrust/braintrust-ui-components";
import { ClearIcon } from "@braintrust/braintrust-ui-components/Icons";
import { SAVED_FILTERS_ENDPOINT } from "@js/apps/common/components/save-filters/const";
import { clearSavedFilterNameIfApplied } from "@js/apps/common/components/save-filters/slice";
import {
  useDeleteJobFilterMutation,
  useSaveJobFilterMutation,
  useUpdateJobFilterNotificationMutation,
} from "@js/apps/jobs/api";
import { DeletedSavedFiltersSnackbarContent } from "@js/apps/jobs/components/deleted-saved-filters-snackbar-content";
import { useGetNotificationsSettingsQuery } from "@js/apps/settings/api";
import { useGetSkillsByIds } from "@js/apps/skills/hooks";
import { Snackbar } from "@js/components/snackbar";
import { useAppDispatch } from "@js/hooks";
import type { Skill } from "@js/types/admin";
import type { SavedJobFilter } from "@js/types/jobs";

import { useGetActiveFilterName } from "../../hooks/active-filter-name";
import { useEmailNotificationTurnedOffSnackBar } from "../../hooks/email-notification-turned-off-snackbar";
import { useSavedSearches } from "../../hooks/saved-searches";
import { SavedJobSearchItem } from "../saved-job-search-item";
import { SavedSearchesEmptyState } from "../saved-searches-empty-state";

import styles from "./style.module.scss";

type SavedJobFiltersDrawerProps = {
  open: boolean;
  setOpen: (input: boolean) => void;
  onChange: (filter: SavedJobFilter) => void;
  location: string;
};

type SavedJobFiltersDrawerContentProps = {
  closeDrawer: () => void;
  onChange: (filter: SavedJobFilter) => void;
  location: string;
  isDrawerOpen: boolean;
};

const SavedJobFiltersDrawerContent: FC<SavedJobFiltersDrawerContentProps> = ({
  closeDrawer,
  onChange,
  location,
  isDrawerOpen,
}) => {
  const dispatch = useAppDispatch();
  const { savedFilterName } = useGetActiveFilterName(
    location,
    SAVED_FILTERS_ENDPOINT.JOBS,
  );
  const { savedFilters, savedFiltersSkillsIds } = useSavedSearches();
  const [updateJobFilterNotification] =
    useUpdateJobFilterNotificationMutation();
  const [deleteJobFilter, { isLoading: isDeletingJobFilter, originalArgs }] =
    useDeleteJobFilterMutation();
  const [saveJobFilter] = useSaveJobFilterMutation();

  const { data } = useGetNotificationsSettingsQuery();

  const { data: skillsData } = useGetSkillsByIds({
    ids: savedFiltersSkillsIds,
  });

  useEmailNotificationTurnedOffSnackBar({
    isDrawerOpen,
  });

  const onEmailNotificationsToggle = (
    event: ChangeEvent<HTMLInputElement>,
    filterId: number,
    enableNotifications: boolean,
  ) => {
    event.stopPropagation();
    updateJobFilterNotification({ filterId, enableNotifications })
      .unwrap()
      .catch(() => null);
  };

  const deleteSavedFilter = async (filter: SavedJobFilter) => {
    const { filters: filtersSaved, name } = filter;
    await deleteJobFilter(filter).unwrap();
    dispatch(clearSavedFilterNameIfApplied(name));

    Snackbar.custom(
      <DeletedSavedFiltersSnackbarContent
        savedFilter={filter}
        onUndoClick={() => {
          saveJobFilter({ filters: filtersSaved, name })
            .unwrap()
            .catch(() => null);
        }}
      />,
      {
        autoHideDuration: 4000,
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "right",
        },
      },
    );
  };

  const onRemoveClick = (filter: SavedJobFilter, event: MouseEvent) => {
    event.stopPropagation();
    void deleteSavedFilter(filter);
  };

  const generateSavedFilters = savedFilters.map((filter) => {
    const isItemProcessing =
      isDeletingJobFilter && originalArgs?.id === filter.id;
    const filterSkills =
      typeof filter?.filters?.skills === "string"
        ? filter.filters.skills
        : null;
    const filterSkillsData: Skill[] = [];
    if (Array.isArray(skillsData) && filterSkills) {
      filterSkills?.split(",").forEach((skillId) => {
        const skillObject = skillsData.find(
          (skill) => skill.id.toString() === skillId,
        );
        if (skillObject) {
          filterSkillsData.push(skillObject);
        }
      });
    }

    return (
      <SavedJobSearchItem
        key={filter.id}
        onApply={() => {
          onChange(filter);
          closeDrawer();
        }}
        onEmailNotificationsToggle={onEmailNotificationsToggle}
        isSelected={filter.name === savedFilterName}
        savedFilter={filter}
        processingSavedFilters={isItemProcessing}
        onRemoveClick={(event) => onRemoveClick(filter, event)}
        filterSkillsData={filterSkillsData}
      />
    );
  });

  const hasNotification = useMemo(() => {
    const isSavedFilterWithNotificationsOn = savedFilters.find(
      (filter) => filter.new_job_notification_enabled === true,
    );
    const isEmailNotificationTurnedOn =
      data?.general_notifications_settings.find(
        (setting) =>
          setting.notification ===
          ENUMS.NotificationTypes.FREELANCER_NEW_JOB_SAVED_FILTERS,
      )?.value;

    return !isEmailNotificationTurnedOn && isSavedFilterWithNotificationsOn;
  }, [savedFilters, data]);

  return (
    <Box
      sx={{
        p: 3,
        width: { xs: "90vw" },
        maxWidth: "590px",
        overflowX: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography component="p" size="small" color="grey">
          Saved searches
        </Typography>
        <IconButton
          variant="transparent"
          onClick={closeDrawer}
          size="medium"
          aria-label="Delete saved search"
        >
          <ClearIcon />
        </IconButton>
      </Box>
      <Grid
        container
        className={cs({
          [styles.notificationBottom]: hasNotification,
        })}
        sx={{ flexDirection: "column" }}
      >
        {savedFilters.length > 0 ? (
          generateSavedFilters
        ) : (
          <SavedSearchesEmptyState closeDrawer={closeDrawer} />
        )}
      </Grid>
    </Box>
  );
};

export const SavedJobFiltersDrawer: FC<SavedJobFiltersDrawerProps> = ({
  open,
  setOpen,
  onChange,
  location,
}) => {
  const closeDrawer = () => {
    setOpen(false);
  };

  const onOpen = () => null;

  return (
    <SwipeableDrawer
      open={open}
      onClose={closeDrawer}
      onOpen={onOpen}
      anchor="right"
      ModalProps={{
        keepMounted: false,
      }}
    >
      <SavedJobFiltersDrawerContent
        closeDrawer={closeDrawer}
        onChange={onChange}
        location={location}
        isDrawerOpen={open}
      />
    </SwipeableDrawer>
  );
};
